import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Stack,
  Avatar,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  LocalShipping,
  Payment,
  Person,
  Phone,
  Email,
  LocationOn,
  Store,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getOrder, updateOrderStatus } from '../store/slices/orderSlice';

const API_URL = process.env.REACT_APP_API_URL || 'https://xaintro-backend.onrender.com';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentOrder, isLoading, error } = useSelector((state) => state.orders);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (id) {
      console.log('🔄 OrderDetail: Fetching order with ID:', id);
      dispatch(getOrder(id))
        .unwrap()
        .then((result) => {
          console.log('✅ OrderDetail: Order fetch successful:', result);
        })
        .catch((error) => {
          console.error('❌ OrderDetail: Order fetch error:', error);
        });
    } else {
      console.warn('⚠️ OrderDetail: No order ID found in URL');
    }
  }, [dispatch, id]);

  useEffect(() => {
    console.log('📊 OrderDetail: Current state:', {
      hasOrder: !!currentOrder,
      isLoading,
      error,
      orderId: id,
      orderData: currentOrder
    });
  }, [currentOrder, isLoading, error, id]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'processing': return 'warning';
      case 'shipped': return 'secondary';
      case 'delivered': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'cashOnDelivery': return 'Cash on Delivery';
      case 'payfast': return 'PayFast';
      case 'bankAlfalah': return 'Bank Alfalah';
      case 'card': return 'Credit/Debit Card';
      case 'paypal': return 'PayPal';
      default: return method || 'Unknown';
    }
  };

  const handleStatusUpdate = (newStatus) => {
    dispatch(updateOrderStatus({ id, statusData: { status: newStatus } }));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/orders')}
          variant="outlined"
        >
          Back to Orders
        </Button>
      </Box>
    );
  }

  if (!currentOrder) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Order not found
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/orders')}
          variant="outlined"
        >
          Back to Orders
        </Button>
      </Box>
    );
  }

  const order = currentOrder;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Header - Mobile Optimized */}
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between" 
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
      >
        <Box display="flex" alignItems="center" gap={2} flexDirection={{ xs: 'column', sm: 'row' }} width={{ xs: '100%', sm: 'auto' }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/orders')}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
          >
            {isMobile ? 'Back' : 'Back to Orders'}
          </Button>
          <Typography 
            variant={isMobile ? "h5" : "h4"}
            sx={{ 
              fontSize: isMobile ? '1.25rem' : '2rem',
              textAlign: { xs: 'center', sm: 'left' }
            }}
          >
            Order #{order.orderNumber || order._id}
          </Typography>
        </Box>
        <Chip
          label={order.status || 'pending'}
          color={getStatusColor(order.status || 'pending')}
          size={isMobile ? "medium" : "large"}
          sx={{ alignSelf: { xs: 'center', sm: 'auto' } }}
        />
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Order Information - Mobile Optimized */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom
                sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
              >
                Order Information
              </Typography>
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    Order Number
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                    {order.orderNumber || order._id}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    Order Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                    {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    Payment Method
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                    {getPaymentMethodLabel(order.paymentMethod)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    Payment Status
                  </Typography>
                  <Chip
                    label={order.paymentStatus || 'pending'}
                    color={order.paymentStatus === 'paid' ? 'success' : 'warning'}
                    size="small"
                    sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Order Items - Mobile Optimized */}
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom
                sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
              >
                Order Items ({order.items?.length || 0})
              </Typography>
              <TableContainer 
                sx={{ 
                  maxHeight: { xs: '300px', sm: 'none' },
                  overflow: { xs: 'auto', sm: 'visible' }
                }}
              >
                <Table size={isMobile ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Product</TableCell>
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Seller</TableCell>
                      <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Qty</TableCell>
                      <TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Price</TableCell>
                      <TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {(item.product?.images?.[0] || item.image) && (
                              <Box
                                component="img"
                                src={
                                  item.product?.images?.[0]?.startsWith('http') 
                                    ? item.product.images[0]
                                    : item.image?.startsWith('http')
                                    ? item.image
                                    : item.product?.images?.[0]
                                    ? `${API_URL}${item.product.images[0]}`
                                    : item.image
                                    ? `${API_URL}${item.image}`
                                    : '/placeholder-product.png'
                                }
                                alt={item.product?.name || item.name || 'Product'}
                                sx={{ 
                                  width: isMobile ? 40 : 50, 
                                  height: isMobile ? 40 : 50, 
                                  borderRadius: 1,
                                  objectFit: 'cover',
                                  border: '1px solid #eee'
                                }}
                                onError={(e) => {
                                  e.target.src = '/placeholder-product.png';
                                }}
                              />
                            )}
                            {!item.product?.images?.[0] && !item.image && (
                              <Box
                                sx={{
                                  width: isMobile ? 40 : 50,
                                  height: isMobile ? 40 : 50,
                                  borderRadius: 1,
                                  bgcolor: '#f5f5f5',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1px solid #eee'
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  No Image
                                </Typography>
                              </Box>
                            )}
                            <Box>
                              <Typography 
                                variant="body2" 
                                fontWeight={600}
                                sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                              >
                                {item.product?.name || item.name}
                              </Typography>
                              {item.product?.brand && (
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary"
                                  sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                                >
                                  {item.product.brand}
                                </Typography>
                              )}
                              {/* Mobile seller info */}
                              <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.5 }}>
                                <Typography 
                                  variant="caption" 
                                  color="primary"
                                  sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', fontWeight: 600 }}
                                >
                                  Seller: {item.product?.seller?.brandName || 
                                          item.product?.seller?.name || 
                                          item.seller?.brandName || 
                                          item.seller?.name || 
                                          'Khizly Store'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                                {item.product?.seller?.brandName || 
                                 item.product?.seller?.name || 
                                 item.seller?.brandName || 
                                 item.seller?.name || 
                                 'Khizly Store'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {item.product?.seller?.email || 
                                 item.seller?.email || 
                                 'seller@khizly.com'}
                              </Typography>
                            </Box>
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <Typography sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                            {item.quantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                            PKR {item.price?.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            fontWeight={600}
                            sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                          >
                            PKR {(item.price * item.quantity).toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
                </Grid>

        {/* Customer & Shipping Info - Mobile Optimized */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom
                sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
              >
                Customer Information
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: '#ff2852', width: isMobile ? 40 : 48, height: isMobile ? 40 : 48 }}>
                  <Person sx={{ fontSize: isMobile ? 20 : 24 }} />
                </Avatar>
                <Box>
                  <Typography 
                    variant="body1" 
                    fontWeight={600}
                    sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                  >
                    {order.user?.name || order.shippingAddress?.name || 'Guest'}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                  >
                    {order.user?.email || 'N/A'}
                  </Typography>
                </Box>
              </Box>
              <Stack spacing={1}>
                {order.shippingAddress?.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Phone sx={{ fontSize: isMobile ? 16 : 20, color: '#666' }} />
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                      {order.shippingAddress.phone}
                    </Typography>
                  </Box>
                )}
                {order.shippingAddress?.address && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationOn sx={{ fontSize: isMobile ? 16 : 20, color: '#666', mt: 0.5 }} />
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                      {order.shippingAddress.address}, {order.shippingAddress?.city}, {order.shippingAddress?.country}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Seller Information - Mobile Optimized */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom
                sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
              >
                Seller Information
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: '#10b981', width: isMobile ? 40 : 48, height: isMobile ? 40 : 48 }}>
                  <Store sx={{ fontSize: isMobile ? 20 : 24 }} />
                </Avatar>
                <Box>
                  <Typography 
                    variant="body1" 
                    fontWeight={600}
                    sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                  >
                    {order.items?.[0]?.product?.seller?.brandName || 
                     order.items?.[0]?.product?.seller?.name || 
                     order.items?.[0]?.seller?.brandName || 
                     order.items?.[0]?.seller?.name || 
                     'Khizly Store'}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                  >
                    {order.items?.[0]?.product?.seller?.email || 
                     order.items?.[0]?.seller?.email || 
                     'seller@khizly.com'}
                  </Typography>
                </Box>
              </Box>
              <Stack spacing={1}>
                {order.items?.[0]?.product?.seller?.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Phone sx={{ fontSize: isMobile ? 16 : 20, color: '#666' }} />
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                      {order.items?.[0]?.product?.seller?.phone}
                    </Typography>
                  </Box>
                )}
                {order.items?.[0]?.product?.seller?.address && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationOn sx={{ fontSize: isMobile ? 16 : 20, color: '#666', mt: 0.5 }} />
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                      {order.items?.[0]?.product?.seller?.address}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={order.items?.[0]?.product?.seller?.sellerStatus || 'verified'}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      bgcolor: order.items?.[0]?.product?.seller?.sellerStatus === 'verified' ? '#e8f5e9' : '#fff3e0',
                      color: order.items?.[0]?.product?.seller?.sellerStatus === 'verified' ? '#2e7d32' : '#ed6c02',
                    }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Order Summary - Mobile Optimized */}
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom
                sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
              >
                Order Summary
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    Subtotal
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    PKR {order.subtotal?.toLocaleString() || '0'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    Shipping
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    PKR {order.shippingCost?.toLocaleString() || '0'}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography 
                    variant="body1" 
                    fontWeight={600}
                    sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                  >
                    Total
                  </Typography>
                  <Typography 
                    variant="body1" 
                    fontWeight={600} 
                    color="#ff2852"
                    sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                  >
                    PKR {order.totalAmount?.toLocaleString() || '0'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Status Update Actions - Mobile Optimized */}
      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            gutterBottom
            sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
          >
            Update Order Status
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 1, sm: 2 }}
            sx={{ flexWrap: 'wrap' }}
          >
            <Button
              variant="contained"
              color="info"
              onClick={() => handleStatusUpdate('confirmed')}
              size={isMobile ? "small" : "medium"}
              sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
            >
              Confirm
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => handleStatusUpdate('processing')}
              size={isMobile ? "small" : "medium"}
              sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
            >
              Processing
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleStatusUpdate('shipped')}
              size={isMobile ? "small" : "medium"}
              sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
            >
              Shipped
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => handleStatusUpdate('delivered')}
              size={isMobile ? "small" : "medium"}
              sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
            >
              Delivered
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleStatusUpdate('cancelled')}
              size={isMobile ? "small" : "medium"}
              sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
            >
              Cancel
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrderDetail;
